import React, { useState } from 'react';
import { V } from '../utils/theme';
import { Icons } from './Icons';
import { Btn } from './ui';
import { Callout, LinkBtn, LogoMark, Notice } from './kit';
import { FullSheet, Switch } from './FullSheet';
import { orgApi, orgErr, errorMessage, consentFailure, SCOPES, CONSENT_DEFAULTS, personName } from '../utils/org';
import { appName } from '../utils/brand';

// The five sharing scopes as equal-weight toggles. Used by the invite consent
// sheet and by Settings → Your gym. `value` is { share_workouts, ... }.
export function ScopeToggles({ value, onChange, disabled }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", borderRadius: 16, border: `1px solid ${V.cardBorder}`, background: V.cardGrad, overflow: "hidden" }}>
      {SCOPES.map((sc, i) => {
        const k = `share_${sc.key}`;
        const on = !!value?.[k];
        return (
          <div key={sc.key} style={{ display: "flex", alignItems: "center", gap: 12, paddingTop: 12, paddingBottom: 12, paddingLeft: 14, paddingRight: 14,
            borderTop: i ? `1px solid ${V.divider}` : "none" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: V.text, lineHeight: 1.3 }}>{sc.label}</div>
              <div style={{ fontSize: 11, color: V.text3, marginTop: 2, lineHeight: 1.4 }}>{sc.desc}</div>
            </div>
            <Switch on={on} disabled={disabled} label={`Share ${sc.label}`} onChange={(v) => onChange({ ...value, [k]: v })} />
          </div>
        );
      })}
    </div>
  );
}

// Full-screen consent step shown right after an invite is redeemed.
// Both buttons write a consent row (so the audit trail exists); "Not now"
// keeps the defaults (workouts and check-ins). No pre-checked extra scopes.
export function ConsentSheet({ orgName, coach, defaults, onDone }) {
  const [value, setValue] = useState({ ...CONSENT_DEFAULTS, ...(defaults || {}) });
  const [busy, setBusy] = useState(false);
  // { retry, message, note, payload } — the failed attempt, kept so Retry re-sends the
  // same choice. Never cleared by closing the sheet: a failure is not a saved choice.
  const [fail, setFail] = useState(null);
  const coachName = coach ? personName(coach) : null;

  const submit = async (payload) => {
    setBusy(true); setFail(null);
    try {
      const r = await orgApi('set_consent', { ...payload, source: 'invite' });
      onDone(r?.consent || payload);
    } catch (e) {
      const er = orgErr(e);
      const f = consentFailure(er);
      // No membership to record against — and nothing was granted either, so let the user through
      if (f.kind === 'no_org') { onDone(null); return; }
      setFail({ retry: f.retry, note: f.note, message: errorMessage(er, orgName), payload });
      setBusy(false);
    }
  };

  return (
    <FullSheet ariaLabel="Sharing with your coach" maxWidth={520}
      footer={
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {fail && (
            <Notice tone="error" center style={{ marginBottom: 4 }}>
              {fail.message}<br />{fail.note}
            </Notice>
          )}
          {fail && !fail.retry ? (
            <Btn full disabled={busy} onClick={() => onDone(null)} s={{ minHeight: 50, fontSize: 15 }}>Continue without saving</Btn>
          ) : (
            <Btn full disabled={busy} onClick={() => submit(fail ? fail.payload : value)} s={{ minHeight: 50, fontSize: 15 }}>
              {busy ? "Saving…" : fail ? "Try again" : "Continue"}
            </Btn>
          )}
          <div style={{ textAlign: "center" }}>
            {fail && fail.retry
              ? <LinkBtn color={V.text3} disabled={busy} onClick={() => onDone(null)}>Continue without saving</LinkBtn>
              : !fail && <LinkBtn color={V.text3} disabled={busy} onClick={() => submit({ ...CONSENT_DEFAULTS })}>Not now</LinkBtn>}
          </div>
        </div>
      }>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8, marginBottom: 18 }}>
        <LogoMark size={48} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", color: V.accent }}>{appName()}</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: V.text, letterSpacing: "-0.01em", lineHeight: 1.15 }}>Welcome to {orgName}</div>
          {coachName && <div style={{ fontSize: 12, color: V.text3, marginTop: 2 }}>Your coach: <span style={{ color: V.text2, fontWeight: 600 }}>{coachName}</span></div>}
        </div>
      </div>

      <Callout icon={Icons.users} style={{ marginBottom: 16 }}>
        <div style={{ color: V.text2 }}>Your coach can always see: your name, when you last used the app, and a summary of how many sessions you log.</div>
      </Callout>

      <div style={{ fontSize: 13, fontWeight: 700, color: V.text, marginBottom: 8 }}>Choose what else to share</div>
      {/* Editing after a failed save keeps the warning up but retries the latest choice */}
      <ScopeToggles value={value} disabled={busy} onChange={(v) => { setValue(v); setFail(f => (f ? { ...f, payload: v } : f)); }} />
      <div style={{ fontSize: 11, color: V.text3, lineHeight: 1.5, marginTop: 12 }}>
        You can change any of these later in Settings → Your gym, or stop sharing everything with one tap. Turning a scope off takes effect immediately.
      </div>
    </FullSheet>
  );
}
